import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ArrowLeft, ArrowRight, Save, CheckCircle, 
  User, Users, Baby, Home, CreditCard, 
  FileText, UserCheck, Info, AlertCircle,
  Plus, Trash2
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_estate-planner-16/artifacts/7xqxcajt_LOGO.jpg';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'personal_info', title: 'Personal Details', icon: User, description: 'Your basic information' },
  { id: 'spouse_info', title: 'Marital Status', icon: Users, description: 'About your spouse' },
  { id: 'children_info', title: 'Children', icon: Baby, description: 'Your children and dependents' },
  { id: 'assets_info', title: 'Assets', icon: Home, description: 'Your property and assets' },
  { id: 'liabilities_info', title: 'Liabilities', icon: CreditCard, description: 'Debts and mortgages' },
  { id: 'distribution_info', title: 'Distribution', icon: FileText, description: 'How to distribute your estate' },
  { id: 'executors_info', title: 'Executors', icon: UserCheck, description: 'Who will manage your estate' },
  { id: 'additional_info', title: 'Review', icon: Info, description: 'Additional information' }
];

export const QuestionnairePage = () => {
  const { willId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [will, setWill] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchWill();
  }, [willId]);

  const fetchWill = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wills/${willId}`, getAuthHeaders());
      setWill(response.data);
      setFormData(response.data.questionnaire);
      // Set current step based on will progress
      const stepIndex = Math.max(0, response.data.current_step - 1);
      setCurrentStep(stepIndex);
    } catch (error) {
      console.error('Error fetching will:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const saveCurrentStep = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const stepId = STEPS[currentStep].id;
      await axios.patch(
        `${API_URL}/api/wills/${willId}`,
        { step: stepId, data: formData[stepId] || {} },
        getAuthHeaders()
      );
      setSaved(true);
      toast.success('Progress saved!');
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Error saving step:', error);
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    await saveCurrentStep();
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleComplete = async () => {
    await saveCurrentStep();
    navigate(`/will/${willId}/review`);
  };

  const updateFormData = (stepId, field, value) => {
    setFormData(prev => ({
      ...prev,
      [stepId]: {
        ...prev[stepId],
        [field]: value
      }
    }));
  };

  const progressPercentage = ((currentStep + 1) / STEPS.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="questionnaire-page">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 text-[#475569] hover:text-[#0A192F]">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Link>
            <img src={LOGO_URL} alt="Clevermoney" className="h-8 w-auto" />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={saveCurrentStep}
              disabled={saving}
              className="border-[#C5A059] text-[#C5A059]"
              data-testid="save-progress-btn"
            >
              {saving ? (
                <>Saving...</>
              ) : saved ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-1 text-[#10B981]" />
                  Saved
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-[#E2E8F0] py-4">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#0A192F]">
              Step {currentStep + 1} of {STEPS.length}
            </span>
            <span className="text-sm text-[#475569]">
              {Math.round(progressPercentage)}% Complete
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
          
          {/* Step indicators */}
          <div className="flex justify-between mt-4">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <div 
                  key={step.id}
                  className={`flex flex-col items-center ${
                    index === 0 ? '' : 'flex-1'
                  }`}
                >
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      isCompleted ? 'bg-[#10B981] text-white' :
                      isActive ? 'bg-[#C5A059] text-white' :
                      'bg-[#E2E8F0] text-[#475569]'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <StepIcon className="w-4 h-4" />
                    )}
                  </div>
                  <span className={`text-xs mt-1 hidden sm:block ${
                    isActive ? 'text-[#0A192F] font-medium' : 'text-[#475569]'
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-[#0A192F] flex items-center gap-3">
              {React.createElement(STEPS[currentStep].icon, { className: "w-6 h-6 text-[#C5A059]" })}
              {STEPS[currentStep].title}
            </CardTitle>
            <CardDescription>{STEPS[currentStep].description}</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Render step content */}
            {currentStep === 0 && (
              <PersonalInfoStep 
                data={formData.personal_info || {}} 
                onChange={(field, value) => updateFormData('personal_info', field, value)} 
              />
            )}
            {currentStep === 1 && (
              <SpouseInfoStep 
                data={formData.spouse_info || {}} 
                onChange={(field, value) => updateFormData('spouse_info', field, value)} 
              />
            )}
            {currentStep === 2 && (
              <ChildrenInfoStep 
                data={formData.children_info || {}} 
                onChange={(field, value) => updateFormData('children_info', field, value)} 
              />
            )}
            {currentStep === 3 && (
              <AssetsInfoStep 
                data={formData.assets_info || {}} 
                onChange={(field, value) => updateFormData('assets_info', field, value)} 
              />
            )}
            {currentStep === 4 && (
              <LiabilitiesInfoStep 
                data={formData.liabilities_info || {}} 
                onChange={(field, value) => updateFormData('liabilities_info', field, value)} 
              />
            )}
            {currentStep === 5 && (
              <DistributionInfoStep 
                data={formData.distribution_info || {}} 
                onChange={(field, value) => updateFormData('distribution_info', field, value)} 
              />
            )}
            {currentStep === 6 && (
              <ExecutorsInfoStep 
                data={formData.executors_info || {}} 
                onChange={(field, value) => updateFormData('executors_info', field, value)}
                hasMinorChildren={formData.children_info?.has_children && formData.children_info?.children?.some(c => c.is_minor)}
              />
            )}
            {currentStep === 7 && (
              <AdditionalInfoStep 
                data={formData.additional_info || {}} 
                onChange={(field, value) => updateFormData('additional_info', field, value)}
                willType={will?.will_type_name}
              />
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-[#E2E8F0]">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                data-testid="prev-step-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              
              {currentStep === STEPS.length - 1 ? (
                <Button 
                  onClick={handleComplete}
                  className="bg-[#C5A059] hover:bg-[#997B40]"
                  data-testid="complete-btn"
                >
                  Review & Complete
                  <CheckCircle className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleNext}
                  className="bg-[#0A192F] hover:bg-[#172A46]"
                  data-testid="next-step-btn"
                >
                  Next Step
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Step Components
const PersonalInfoStep = ({ data, onChange }) => (
  <div className="space-y-6" data-testid="personal-info-step">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Full Name *</Label>
        <Input
          value={data.full_name || ''}
          onChange={(e) => onChange('full_name', e.target.value)}
          placeholder="Enter your full legal name"
          data-testid="personal-full-name"
        />
      </div>
      <div className="space-y-2">
        <Label>Date of Birth</Label>
        <Input
          type="date"
          value={data.birth_date || ''}
          onChange={(e) => onChange('birth_date', e.target.value)}
          data-testid="personal-birth-date"
        />
      </div>
    </div>
    
    <div className="space-y-2">
      <Label>Gender *</Label>
      <RadioGroup
        value={data.gender || ''}
        onValueChange={(value) => onChange('gender', value)}
        className="flex gap-4"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="male" id="gender-male" data-testid="gender-male" />
          <Label htmlFor="gender-male">Male</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="female" id="gender-female" data-testid="gender-female" />
          <Label htmlFor="gender-female">Female</Label>
        </div>
      </RadioGroup>
      <p className="text-xs text-[#475569]">This helps us select the correct will template language</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>PPS Number</Label>
        <Input
          value={data.pps_number || ''}
          onChange={(e) => onChange('pps_number', e.target.value)}
          placeholder="e.g., 1234567A"
          data-testid="personal-pps"
        />
      </div>
      <div className="space-y-2">
        <Label>Occupation</Label>
        <Input
          value={data.occupation || ''}
          onChange={(e) => onChange('occupation', e.target.value)}
          placeholder="Your occupation"
          data-testid="personal-occupation"
        />
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Mobile Phone</Label>
        <Input
          value={data.mobile || ''}
          onChange={(e) => onChange('mobile', e.target.value)}
          placeholder="+353 87 123 4567"
          data-testid="personal-mobile"
        />
      </div>
      <div className="space-y-2">
        <Label>Email Address</Label>
        <Input
          type="email"
          value={data.home_email || ''}
          onChange={(e) => onChange('home_email', e.target.value)}
          placeholder="you@example.com"
          data-testid="personal-email"
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label>Home Address *</Label>
      <Textarea
        value={data.home_address || ''}
        onChange={(e) => onChange('home_address', e.target.value)}
        placeholder="Enter your full home address"
        rows={2}
        data-testid="personal-address"
      />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>County *</Label>
        <Input
          value={data.county || ''}
          onChange={(e) => onChange('county', e.target.value)}
          placeholder="e.g., Dublin"
          data-testid="personal-county"
        />
      </div>
      <div className="space-y-2">
        <Label>Eircode</Label>
        <Input
          value={data.eircode || ''}
          onChange={(e) => onChange('eircode', e.target.value)}
          placeholder="e.g., D01 ABC1"
          data-testid="personal-eircode"
        />
      </div>
    </div>
  </div>
);

const SpouseInfoStep = ({ data, onChange }) => (
  <div className="space-y-6" data-testid="spouse-info-step">
    <div className="space-y-4">
      <Label className="text-base font-medium">Are you married?</Label>
      <RadioGroup
        value={data.is_married ? 'yes' : 'no'}
        onValueChange={(value) => onChange('is_married', value === 'yes')}
        className="flex gap-6"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="yes" id="married-yes" data-testid="married-yes" />
          <Label htmlFor="married-yes">Yes</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="no" id="married-no" data-testid="married-no" />
          <Label htmlFor="married-no">No</Label>
        </div>
      </RadioGroup>
    </div>

    {data.is_married && (
      <div className="space-y-4 pt-4 border-t border-[#E2E8F0] animate-fade-in">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Spouse's Full Name *</Label>
            <Input
              value={data.spouse_full_name || ''}
              onChange={(e) => onChange('spouse_full_name', e.target.value)}
              placeholder="Enter spouse's full name"
              data-testid="spouse-name"
            />
          </div>
          <div className="space-y-2">
            <Label>Spouse's Date of Birth</Label>
            <Input
              type="date"
              value={data.spouse_birth_date || ''}
              onChange={(e) => onChange('spouse_birth_date', e.target.value)}
              data-testid="spouse-dob"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Date and Place of Marriage</Label>
          <Input
            value={data.date_place_marriage || ''}
            onChange={(e) => onChange('date_place_marriage', e.target.value)}
            placeholder="e.g., 15th June 2010, Dublin"
            data-testid="marriage-date-place"
          />
        </div>

        <div className="space-y-2">
          <Label>Maiden Name (if applicable)</Label>
          <Input
            value={data.maiden_name || ''}
            onChange={(e) => onChange('maiden_name', e.target.value)}
            placeholder="Spouse's maiden name"
            data-testid="maiden-name"
          />
        </div>

        <div className="space-y-2">
          <Label>Previous Marriages</Label>
          <Textarea
            value={data.previous_marriages || ''}
            onChange={(e) => onChange('previous_marriages', e.target.value)}
            placeholder="If either of you were previously married, list details here"
            rows={3}
            data-testid="previous-marriages"
          />
        </div>
      </div>
    )}
  </div>
);

const ChildrenInfoStep = ({ data, onChange }) => {
  const children = data.children || [];

  const addChild = () => {
    onChange('children', [...children, { full_name: '', birth_date: '', address: '', is_minor: true, is_living: true }]);
  };

  const removeChild = (index) => {
    onChange('children', children.filter((_, i) => i !== index));
  };

  const updateChild = (index, field, value) => {
    const updated = [...children];
    updated[index] = { ...updated[index], [field]: value };
    onChange('children', updated);
  };

  return (
    <div className="space-y-6" data-testid="children-info-step">
      <div className="space-y-4">
        <Label className="text-base font-medium">Do you have any children?</Label>
        <RadioGroup
          value={data.has_children ? 'yes' : 'no'}
          onValueChange={(value) => {
            onChange('has_children', value === 'yes');
            if (value === 'no') onChange('children', []);
          }}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="children-yes" data-testid="children-yes" />
            <Label htmlFor="children-yes">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="children-no" data-testid="children-no" />
            <Label htmlFor="children-no">No</Label>
          </div>
        </RadioGroup>
      </div>

      {data.has_children && (
        <div className="space-y-4 pt-4 border-t border-[#E2E8F0] animate-fade-in">
          {children.map((child, index) => (
            <Card key={index} className="p-4 bg-[#FDFBF7]" data-testid={`child-${index}`}>
              <div className="flex justify-between items-start mb-4">
                <h4 className="font-medium text-[#0A192F]">Child {index + 1}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeChild(index)}
                  className="text-[#EF4444] hover:text-[#DC2626]"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={child.full_name || ''}
                    onChange={(e) => updateChild(index, 'full_name', e.target.value)}
                    placeholder="Child's full name"
                    data-testid={`child-name-${index}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={child.birth_date || ''}
                    onChange={(e) => updateChild(index, 'birth_date', e.target.value)}
                    data-testid={`child-dob-${index}`}
                  />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <Label>Address (if different from yours)</Label>
                <Input
                  value={child.address || ''}
                  onChange={(e) => updateChild(index, 'address', e.target.value)}
                  placeholder="Child's address if not living with you"
                  data-testid={`child-address-${index}`}
                />
              </div>
              <div className="mt-4 flex gap-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`minor-${index}`}
                    checked={child.is_minor}
                    onCheckedChange={(checked) => updateChild(index, 'is_minor', checked)}
                    data-testid={`child-minor-${index}`}
                  />
                  <Label htmlFor={`minor-${index}`}>Under 18 years old</Label>
                </div>
              </div>
            </Card>
          ))}
          
          <Button
            variant="outline"
            onClick={addChild}
            className="w-full border-dashed"
            data-testid="add-child-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Child
          </Button>
        </div>
      )}
    </div>
  );
};

const AssetsInfoStep = ({ data, onChange }) => {
  const properties = data.properties || [];

  const addProperty = () => {
    onChange('properties', [...properties, { description: '', location: '', value: '', title_held: '' }]);
  };

  const removeProperty = (index) => {
    onChange('properties', properties.filter((_, i) => i !== index));
  };

  const updateProperty = (index, field, value) => {
    const updated = [...properties];
    updated[index] = { ...updated[index], [field]: value };
    onChange('properties', updated);
  };

  return (
    <div className="space-y-6" data-testid="assets-info-step">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          List your main assets. This helps us understand your estate but isn't legally binding - you can provide more detail in the specific bequests section.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <h4 className="font-medium text-[#0A192F]">Properties (Land & Buildings)</h4>
        {properties.map((property, index) => (
          <Card key={index} className="p-4 bg-[#FDFBF7]" data-testid={`property-${index}`}>
            <div className="flex justify-between items-start mb-4">
              <h5 className="text-sm font-medium">Property {index + 1}</h5>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeProperty(index)}
                className="text-[#EF4444]"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={property.description || ''}
                  onChange={(e) => updateProperty(index, 'description', e.target.value)}
                  placeholder="e.g., Family home"
                  data-testid={`property-desc-${index}`}
                />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={property.location || ''}
                  onChange={(e) => updateProperty(index, 'location', e.target.value)}
                  placeholder="Address"
                  data-testid={`property-location-${index}`}
                />
              </div>
              <div className="space-y-2">
                <Label>Estimated Value (€)</Label>
                <Input
                  value={property.value || ''}
                  onChange={(e) => updateProperty(index, 'value', e.target.value)}
                  placeholder="e.g., 350,000"
                  data-testid={`property-value-${index}`}
                />
              </div>
              <div className="space-y-2">
                <Label>Title Held By</Label>
                <Input
                  value={property.title_held || ''}
                  onChange={(e) => updateProperty(index, 'title_held', e.target.value)}
                  placeholder="e.g., Joint ownership"
                  data-testid={`property-title-${index}`}
                />
              </div>
            </div>
          </Card>
        ))}
        <Button
          variant="outline"
          onClick={addProperty}
          className="w-full border-dashed"
          data-testid="add-property-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Property
        </Button>
      </div>

      <div className="space-y-2">
        <Label>Overseas Holdings (if any)</Label>
        <Textarea
          value={data.overseas_holdings || ''}
          onChange={(e) => onChange('overseas_holdings', e.target.value)}
          placeholder="Details of any property or assets held outside Ireland"
          rows={2}
          data-testid="overseas-holdings"
        />
      </div>

      <div className="space-y-2">
        <Label>Total Estimated Value of Assets (€)</Label>
        <Input
          value={data.total_assets || ''}
          onChange={(e) => onChange('total_assets', e.target.value)}
          placeholder="e.g., 500,000"
          data-testid="total-assets"
        />
      </div>
    </div>
  );
};

const LiabilitiesInfoStep = ({ data, onChange }) => {
  const mortgages = data.mortgages || [];

  const addMortgage = () => {
    onChange('mortgages', [...mortgages, { description: '', amount: '' }]);
  };

  const removeMortgage = (index) => {
    onChange('mortgages', mortgages.filter((_, i) => i !== index));
  };

  const updateMortgage = (index, field, value) => {
    const updated = [...mortgages];
    updated[index] = { ...updated[index], [field]: value };
    onChange('mortgages', updated);
  };

  return (
    <div className="space-y-6" data-testid="liabilities-info-step">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Your executor will pay all debts from your estate before distributing to beneficiaries.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <h4 className="font-medium text-[#0A192F]">Mortgages & Loans</h4>
        {mortgages.map((mortgage, index) => (
          <Card key={index} className="p-4 bg-[#FDFBF7]" data-testid={`mortgage-${index}`}>
            <div className="flex justify-between items-start mb-4">
              <h5 className="text-sm font-medium">Liability {index + 1}</h5>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeMortgage(index)}
                className="text-[#EF4444]"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={mortgage.description || ''}
                  onChange={(e) => updateMortgage(index, 'description', e.target.value)}
                  placeholder="e.g., Home mortgage with AIB"
                  data-testid={`mortgage-desc-${index}`}
                />
              </div>
              <div className="space-y-2">
                <Label>Amount Outstanding (€)</Label>
                <Input
                  value={mortgage.amount || ''}
                  onChange={(e) => updateMortgage(index, 'amount', e.target.value)}
                  placeholder="e.g., 150,000"
                  data-testid={`mortgage-amount-${index}`}
                />
              </div>
            </div>
          </Card>
        ))}
        <Button
          variant="outline"
          onClick={addMortgage}
          className="w-full border-dashed"
          data-testid="add-mortgage-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Mortgage/Loan
        </Button>
      </div>

      <div className="space-y-2">
        <Label>Total Liabilities (€)</Label>
        <Input
          value={data.total_liabilities || ''}
          onChange={(e) => onChange('total_liabilities', e.target.value)}
          placeholder="e.g., 150,000"
          data-testid="total-liabilities"
        />
      </div>
    </div>
  );
};

const DistributionInfoStep = ({ data, onChange }) => {
  const beneficiaries = data.residuary_beneficiaries || [];

  const addBeneficiary = () => {
    onChange('residuary_beneficiaries', [...beneficiaries, { full_name: '', address: '', relationship: '', share_percentage: '' }]);
  };

  const removeBeneficiary = (index) => {
    onChange('residuary_beneficiaries', beneficiaries.filter((_, i) => i !== index));
  };

  const updateBeneficiary = (index, field, value) => {
    const updated = [...beneficiaries];
    updated[index] = { ...updated[index], [field]: value };
    onChange('residuary_beneficiaries', updated);
  };

  return (
    <div className="space-y-6" data-testid="distribution-info-step">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          The residuary beneficiaries receive the remainder of your estate after specific bequests and debts are paid.
        </AlertDescription>
      </Alert>

      <div className="space-y-2">
        <Label>Distribution Plan Overview</Label>
        <Textarea
          value={data.distribution_plan || ''}
          onChange={(e) => onChange('distribution_plan', e.target.value)}
          placeholder="Describe in general terms how you wish to leave your property at death"
          rows={3}
          data-testid="distribution-plan"
        />
      </div>

      <div className="space-y-4">
        <h4 className="font-medium text-[#0A192F]">Residuary Beneficiaries</h4>
        <p className="text-sm text-[#475569]">Who should receive the remainder of your estate?</p>
        
        {beneficiaries.map((beneficiary, index) => (
          <Card key={index} className="p-4 bg-[#FDFBF7]" data-testid={`beneficiary-${index}`}>
            <div className="flex justify-between items-start mb-4">
              <h5 className="text-sm font-medium">Beneficiary {index + 1}</h5>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeBeneficiary(index)}
                className="text-[#EF4444]"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={beneficiary.full_name || ''}
                  onChange={(e) => updateBeneficiary(index, 'full_name', e.target.value)}
                  placeholder="Beneficiary's full name"
                  data-testid={`beneficiary-name-${index}`}
                />
              </div>
              <div className="space-y-2">
                <Label>Relationship</Label>
                <Input
                  value={beneficiary.relationship || ''}
                  onChange={(e) => updateBeneficiary(index, 'relationship', e.target.value)}
                  placeholder="e.g., Son, Daughter, Friend"
                  data-testid={`beneficiary-relationship-${index}`}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Address</Label>
                <Input
                  value={beneficiary.address || ''}
                  onChange={(e) => updateBeneficiary(index, 'address', e.target.value)}
                  placeholder="Beneficiary's address"
                  data-testid={`beneficiary-address-${index}`}
                />
              </div>
              <div className="space-y-2">
                <Label>Share (%)</Label>
                <Input
                  value={beneficiary.share_percentage || ''}
                  onChange={(e) => updateBeneficiary(index, 'share_percentage', e.target.value)}
                  placeholder="e.g., 50"
                  data-testid={`beneficiary-share-${index}`}
                />
              </div>
            </div>
          </Card>
        ))}
        
        <Button
          variant="outline"
          onClick={addBeneficiary}
          className="w-full border-dashed"
          data-testid="add-beneficiary-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Beneficiary
        </Button>
      </div>

      <div className="space-y-2">
        <Label>Charity Bequests (optional)</Label>
        <Textarea
          value={data.charity_bequests || ''}
          onChange={(e) => onChange('charity_bequests', e.target.value)}
          placeholder="Any charitable donations you wish to make"
          rows={2}
          data-testid="charity-bequests"
        />
      </div>
    </div>
  );
};

const ExecutorsInfoStep = ({ data, onChange, hasMinorChildren }) => {
  const executors = data.executors || [];
  const guardians = data.guardians || [];

  const addExecutor = () => {
    const roles = ['primary', 'first_alternate', 'second_alternate'];
    const nextRole = roles[executors.length] || 'additional';
    onChange('executors', [...executors, { full_name: '', address: '', telephone: '', role: nextRole }]);
  };

  const removeExecutor = (index) => {
    onChange('executors', executors.filter((_, i) => i !== index));
  };

  const updateExecutor = (index, field, value) => {
    const updated = [...executors];
    updated[index] = { ...updated[index], [field]: value };
    onChange('executors', updated);
  };

  const addGuardian = () => {
    const roles = ['primary', 'first_alternate'];
    const nextRole = roles[guardians.length] || 'additional';
    onChange('guardians', [...guardians, { full_name: '', address: '', telephone: '', role: nextRole }]);
  };

  const removeGuardian = (index) => {
    onChange('guardians', guardians.filter((_, i) => i !== index));
  };

  const updateGuardian = (index, field, value) => {
    const updated = [...guardians];
    updated[index] = { ...updated[index], [field]: value };
    onChange('guardians', updated);
  };

  const roleLabels = {
    'primary': 'Primary',
    'first_alternate': 'First Alternate',
    'second_alternate': 'Second Alternate'
  };

  return (
    <div className="space-y-6" data-testid="executors-info-step">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Your executor is responsible for managing your estate and ensuring your wishes are carried out.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <h4 className="font-medium text-[#0A192F]">Executors & Trustees</h4>
        
        {executors.map((executor, index) => (
          <Card key={index} className="p-4 bg-[#FDFBF7]" data-testid={`executor-${index}`}>
            <div className="flex justify-between items-start mb-4">
              <h5 className="text-sm font-medium">
                {roleLabels[executor.role] || 'Additional'} Executor
              </h5>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeExecutor(index)}
                className="text-[#EF4444]"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={executor.full_name || ''}
                  onChange={(e) => updateExecutor(index, 'full_name', e.target.value)}
                  placeholder="Executor's full name"
                  data-testid={`executor-name-${index}`}
                />
              </div>
              <div className="space-y-2">
                <Label>Telephone</Label>
                <Input
                  value={executor.telephone || ''}
                  onChange={(e) => updateExecutor(index, 'telephone', e.target.value)}
                  placeholder="Phone number"
                  data-testid={`executor-phone-${index}`}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Address *</Label>
                <Input
                  value={executor.address || ''}
                  onChange={(e) => updateExecutor(index, 'address', e.target.value)}
                  placeholder="Executor's address"
                  data-testid={`executor-address-${index}`}
                />
              </div>
            </div>
          </Card>
        ))}
        
        {executors.length < 3 && (
          <Button
            variant="outline"
            onClick={addExecutor}
            className="w-full border-dashed"
            data-testid="add-executor-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Executor
          </Button>
        )}
      </div>

      {hasMinorChildren && (
        <div className="space-y-4 pt-6 border-t border-[#E2E8F0]">
          <h4 className="font-medium text-[#0A192F]">Guardians of Minor Children</h4>
          <p className="text-sm text-[#475569]">
            Since you have minor children, you should appoint guardians who will care for them if needed.
          </p>
          
          {guardians.map((guardian, index) => (
            <Card key={index} className="p-4 bg-[#FDFBF7]" data-testid={`guardian-${index}`}>
              <div className="flex justify-between items-start mb-4">
                <h5 className="text-sm font-medium">
                  {roleLabels[guardian.role] || 'Additional'} Guardian
                </h5>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeGuardian(index)}
                  className="text-[#EF4444]"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={guardian.full_name || ''}
                    onChange={(e) => updateGuardian(index, 'full_name', e.target.value)}
                    placeholder="Guardian's full name"
                    data-testid={`guardian-name-${index}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telephone</Label>
                  <Input
                    value={guardian.telephone || ''}
                    onChange={(e) => updateGuardian(index, 'telephone', e.target.value)}
                    placeholder="Phone number"
                    data-testid={`guardian-phone-${index}`}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Address *</Label>
                  <Input
                    value={guardian.address || ''}
                    onChange={(e) => updateGuardian(index, 'address', e.target.value)}
                    placeholder="Guardian's address"
                    data-testid={`guardian-address-${index}`}
                  />
                </div>
              </div>
            </Card>
          ))}
          
          {guardians.length < 2 && (
            <Button
              variant="outline"
              onClick={addGuardian}
              className="w-full border-dashed"
              data-testid="add-guardian-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Guardian
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const AdditionalInfoStep = ({ data, onChange, willType }) => (
  <div className="space-y-6" data-testid="additional-info-step">
    <Alert className="bg-[#C5A059]/10 border-[#C5A059]">
      <CheckCircle className="h-4 w-4 text-[#C5A059]" />
      <AlertDescription className="text-[#0A192F]">
        <strong>Will Type Selected:</strong> {willType}
        <br />
        <span className="text-sm text-[#475569]">Based on your answers, this is the most appropriate will template for your situation.</span>
      </AlertDescription>
    </Alert>

    <div className="space-y-2">
      <Label>Funeral Plan (Optional)</Label>
      <Textarea
        value={data.funeral_plan || ''}
        onChange={(e) => onChange('funeral_plan', e.target.value)}
        placeholder="Describe any preferences for your funeral and burial arrangements"
        rows={4}
        data-testid="funeral-plan"
      />
    </div>

    <div className="p-4 bg-[#FDFBF7] rounded-lg border border-[#E2E8F0]">
      <h4 className="font-medium text-[#0A192F] mb-2">What Happens Next?</h4>
      <ol className="list-decimal list-inside space-y-2 text-sm text-[#475569]">
        <li>Review your generated will document</li>
        <li>Download both the questionnaire and will as PDF</li>
        <li>Print the will and sign it in the presence of two witnesses</li>
        <li>Both witnesses must sign the will</li>
        <li>Store your will in a safe place and inform your executor</li>
      </ol>
    </div>

    <Alert variant="default">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>
        <strong>Important:</strong> We recommend consulting a solicitor if you have complex assets, 
        overseas property, or any concerns about your will. This service provides standard will 
        templates approved by the Law Society of Ireland.
      </AlertDescription>
    </Alert>
  </div>
);

export default QuestionnairePage;
